import { useEffect, useMemo, useRef, useState } from "react";
import { IconX, IconUpload, IconFileText } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Input } from "../../../../components/common/input";
import { IconPill } from "../../../../components/common/icon-pill";
import { Button } from "../../../../components/common/button";
import { DatePicker } from "../../../../components/common/date-picker";
import { TimePicker } from "../../../../components/common/time-picker";

interface Props {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
  onConfirm: (imageFile: File) => Promise<void>;
}

export function MarkAsDeliveredModal({ isOpen, orderId, onClose, onConfirm }: Props) {
  const [deliveredBy, setDeliveredBy]           = useState("");
  const [deliveryDate, setDeliveryDate]         = useState("");
  const [deliveryTime, setDeliveryTime]         = useState("12:00");
  const [receivedBy, setReceivedBy]             = useState("");
  const [recipientRole, setRecipientRole]       = useState("");
  const [note, setNote]                         = useState("");
  const [files, setFiles]                       = useState<File[]>([]);
  const [dragging, setDragging]                 = useState(false);
  const [isSubmitting, setIsSubmitting]         = useState(false);
  const fileRef                                 = useRef<HTMLInputElement>(null);

  const handleDevAutofill = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    setDeliveredBy("Ahmed Al-Khatib (DHL)");
    setDeliveryDate(`${y}-${m}-${d}`);
    setDeliveryTime(`${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`);
    setReceivedBy("Sarah Al-Mannai");
    setRecipientRole("Procurement Manager");
    setNote("Delivered to reception desk, signed by procurement team.");
  };

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...dropped]);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  // Build object-URLs for image files so we can render thumbnail previews.
  // Revoke them on cleanup or whenever the file list changes to avoid leaking
  // blob references in browser memory.
  const previews = useMemo(
    () =>
      files.map((file) => ({
        file,
        isImage: file.type.startsWith("image/"),
        objectUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      })),
    [files],
  );
  useEffect(() => {
    return () => {
      previews.forEach((p) => {
        if (p.objectUrl) URL.revokeObjectURL(p.objectUrl);
      });
    };
  }, [previews]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-full max-w-[610px] rounded-2xl bg-bgWhite p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between">
          <Heading variant="h3" tone="dark">Mark as Delivered — {orderId}</Heading>
          <div className="flex items-center gap-2">
            {import.meta.env.DEV && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDevAutofill}
                disabled={isSubmitting}
              >
                Dev autofill
              </Button>
            )}
            <IconPill
              as="button"
              size={32}
              tone="ghost"
              aria-label="Close"
              onClick={onClose}
              className="!text-textMuted hover:!bg-bgLight hover:!text-textDark"
            >
              <IconX size={18} />
            </IconPill>
          </div>
        </div>

        <div className="my-5 border-t border-borderLight" />

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Text variant="bodyBold" tone="dark">Delivered by (carrier / driver)</Text>
              <Input value={deliveredBy} onChange={(e) => setDeliveredBy(e.target.value)} placeholder="e.g. Ahmed Al-Khatib (DHL)" className="placeholder:!text-textMuted" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Text variant="bodyBold" tone="dark">Delivery Date & Time</Text>
              <div className="grid grid-cols-2 gap-2">
                <DatePicker value={deliveryDate} onChange={setDeliveryDate} size="lg" />
                <TimePicker value={deliveryTime} onChange={setDeliveryTime} size="lg" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Text variant="bodyBold" tone="dark">Received by (name)</Text>
              <Input value={receivedBy} onChange={(e) => setReceivedBy(e.target.value)} placeholder="e.g. Sarah Al-Mannai" className="placeholder:!text-textMuted" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Text variant="bodyBold" tone="dark">Recipient Title / Role</Text>
              <Input value={recipientRole} onChange={(e) => setRecipientRole(e.target.value)} placeholder="e.g. Procurement Manager" className="placeholder:!text-textMuted" />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Text variant="bodyBold" tone="dark">Proof of Delivery (photos / signature)</Text>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              className={`flex min-h-[110px] cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed transition-colors ${
                dragging ? "border-accentCyan bg-accentCyanTint" : "border-borderDark bg-bgLight"
              }`}
            >
              {files.length === 0 ? (
                <Text variant="bodySm" tone="muted">
                  Drag & drop photos here or click to browse &nbsp;(JPG, PNG, PDF)
                </Text>
              ) : (
                <div className="flex flex-col items-center gap-1">
                  <IconUpload size={18} className="text-textMuted" />
                  <Text variant="bodySm" tone="muted">
                    {files.length} file{files.length > 1 ? "s" : ""} selected — click to add more
                  </Text>
                </div>
              )}
              <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={handleFileChange} />
            </div>

            {previews.length > 0 && (
              <div className="mt-2 grid grid-cols-3 gap-3">
                {previews.map((p, index) => (
                  <div
                    key={`${p.file.name}-${index}`}
                    className="relative flex h-28 flex-col items-center justify-center overflow-hidden rounded-xl border border-borderDefault bg-bgLight"
                  >
                    {p.isImage && p.objectUrl ? (
                      <img
                        src={p.objectUrl}
                        alt={p.file.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1 px-2 text-center">
                        <IconFileText size={22} className="text-textMuted" />
                        <Text variant="bodyXs" tone="muted" className="line-clamp-2">
                          {p.file.name}
                        </Text>
                      </div>
                    )}
                    <button
                      type="button"
                      aria-label={`Remove ${p.file.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      className="absolute right-1.5 top-1.5 inline-flex size-5 items-center justify-center rounded-full bg-bgWhite/90 text-textDark shadow"
                    >
                      <IconX size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Text variant="bodyBold" tone="dark">Note (optional)</Text>
            <Input
              multiline
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Delivered to reception desk, signed by procurement team."
              className="placeholder:!text-textMuted"
            />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <Button
            variant="primary"
            size="md"
            fullWidth
            className="!bg-textDark !border-textDark"
            disabled={files.length === 0 || isSubmitting}
            onClick={async () => {
              if (files.length === 0 || isSubmitting) return;
              setIsSubmitting(true);
              try {
                await onConfirm(files[0]);
                onClose();
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? "Submitting..." : "Confirm Delivery"}
          </Button>
          <Button variant="secondary" size="md" fullWidth onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
